import React from 'react';
import { Card, CardBody, CardHeader } from './../../components/card/card.jsx';

/**
 * Comprehensive documentation for the Small Pentest feature
 */
const SmallPentestDocumentation = () => {
  return (
    <div className="small-pentest-documentation">
      <h2 className="mb-4">
        <i className="bi bi-shield-check me-2"></i>
        Small Pentest Documentation
      </h2>

      {/* Overview Section */}
      <Card className="mb-4">
        <CardHeader>
          <h4 className="mb-0">
            <i className="bi bi-info-circle me-2"></i>
            Overview
          </h4>
        </CardHeader>
        <CardBody>
          <p>
            The Small Pentest module is a comprehensive security testing platform that allows users to perform 
            automated penetration testing on web applications and network targets. It provides a user-friendly 
            interface for selecting security tools, monitoring scan progress, and analyzing results.
          </p>
          <h5>Key Features:</h5>
          <ul>
            <li>Multi-tool security scanning with role-based access control</li>
            <li>Real-time progress monitoring and logging</li>
            <li>Comprehensive result analysis and file management</li>
            <li>AI-powered PDF report generation</li>
            <li>Scan history and data management</li>
          </ul>
        </CardBody>
      </Card>

      {/* User Permissions Section */}
      <Card className="mb-4">
        <CardHeader>
          <h4 className="mb-0">
            <i className="bi bi-person-check me-2"></i>
            User Permissions & Roles
          </h4>
        </CardHeader>
        <CardBody>
          <h5>Role Hierarchy:</h5>
          <div className="row">
            <div className="col-md-4">
              <div className="card border-success">
                <div className="card-header bg-success text-white">
                  <strong>User (US)</strong>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled">
                    <li>✓ Access basic scanning tools</li>
                    <li>✓ View own scan results</li>
                    <li>✓ Monitor scan progress</li>
                    <li>✗ Delete scans</li>
                    <li>✗ Generate PDF reports</li>
                    <li>✗ Access advanced tools</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-warning">
                <div className="card-header bg-warning text-dark">
                  <strong>Superuser (SU)</strong>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled">
                    <li>✓ All User permissions</li>
                    <li>✓ Access advanced scanning tools</li>
                    <li>✓ Generate AI PDF reports</li>
                    <li>✓ Delete individual scans</li>
                    <li>✓ View all scan results</li>
                    <li>✓ Bulk operations</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-danger">
                <div className="card-header bg-danger text-white">
                  <strong>Admin (AD)</strong>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled">
                    <li>✓ All Superuser permissions</li>
                    <li>✓ Access all security tools</li>
                    <li>✓ System administration</li>
                    <li>✓ Bulk scan deletion</li>
                    <li>✓ Clear table data</li>
                    <li>✓ Full system control</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Tool Categories Section */}
      <Card className="mb-4">
        <CardHeader>
          <h4 className="mb-0">
            <i className="bi bi-tools me-2"></i>
            Available Security Tools
          </h4>
        </CardHeader>
        <CardBody>
          <div className="alert alert-info mb-4">
            <i className="fa fa-info-circle me-2"></i>
            <strong>Note:</strong> Tool availability depends on your user role. Higher-privilege tools require 
            Superuser or Admin roles for access. Each tool below shows what it tests and its risk level.
          </div>

          {/* Reconnaissance Tools */}
          <h5><i className="fa fa-search text-secondary me-2"></i>Reconnaissance Tools</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="card border-secondary mb-3">
                <div className="card-header bg-secondary text-white">
                  <strong>DNS Reconnaissance</strong> <span className="badge bg-success ms-2">US</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> DNS records, subdomains, mail servers, and DNS configuration</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-success">Low</span></p>
                  <small className="text-muted">Safe for all environments - only performs DNS lookups</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-secondary mb-3">
                <div className="card-header bg-secondary text-white">
                  <strong>Port Scanner (TCP)</strong> <span className="badge bg-success ms-2">US</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Open TCP ports and running services using Nmap</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-warning">Medium</span></p>
                  <small className="text-muted">May trigger security alerts - scans network ports</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-secondary mb-3">
                <div className="card-header bg-secondary text-white">
                  <strong>Subdomain Enumeration</strong> <span className="badge bg-success ms-2">US</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Discovers subdomains using multiple online sources</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-success">Low</span></p>
                  <small className="text-muted">Passive reconnaissance - uses public databases</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-secondary mb-3">
                <div className="card-header bg-secondary text-white">
                  <strong>UDP Port Scanner</strong> <span className="badge bg-warning ms-2">SU</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Open UDP ports and services</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-warning">Medium</span></p>
                  <small className="text-muted">Network scanning - may be detected by monitoring systems</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-secondary mb-3">
                <div className="card-header bg-secondary text-white">
                  <strong>Directory/File Fuzzer</strong> <span className="badge bg-warning ms-2">SU</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Hidden directories and files through brute-forcing</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-danger">High</span></p>
                  <small className="text-muted">Aggressive scanning - generates many requests</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-secondary mb-3">
                <div className="card-header bg-secondary text-white">
                  <strong>JavaScript Endpoint Parser</strong> <span className="badge bg-success ms-2">US</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> JavaScript files for hidden endpoints and API keys</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-success">Low</span></p>
                  <small className="text-muted">Static analysis - examines client-side code</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-secondary mb-3">
                <div className="card-header bg-secondary text-white">
                  <strong>API Endpoint Discovery</strong> <span className="badge bg-success ms-2">US</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> OpenAPI/Swagger/GraphQL endpoints and documentation</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-success">Low</span></p>
                  <small className="text-muted">Discovers API documentation and endpoints</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-secondary mb-3">
                <div className="card-header bg-secondary text-white">
                  <strong>EDR/AV Detection Scanner</strong> <span className="badge bg-success ms-2">US</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Detects endpoint security solutions and antivirus</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-success">Low</span></p>
                  <small className="text-muted">Passive detection - identifies security products</small>
                </div>
              </div>
            </div>
          </div>

          {/* Security Assessment Tools */}
          <h5><i className="fa fa-shield-alt text-primary me-2"></i>Security Assessment Tools</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="card border-primary mb-3">
                <div className="card-header bg-primary text-white">
                  <strong>SSL/TLS Analysis</strong> <span className="badge bg-success ms-2">US</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> SSL/TLS certificates, protocols, and cipher suites</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-success">Low</span></p>
                  <small className="text-muted">Analyzes encryption configuration and certificate validity</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-primary mb-3">
                <div className="card-header bg-primary text-white">
                  <strong>Security Headers Check</strong> <span className="badge bg-success ms-2">US</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> HTTP security headers (CSP, HSTS, X-Frame-Options, etc.)</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-success">Low</span></p>
                  <small className="text-muted">Checks for security-related HTTP response headers</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-primary mb-3">
                <div className="card-header bg-primary text-white">
                  <strong>CORS Misconfiguration Check</strong> <span className="badge bg-warning ms-2">SU</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Cross-Origin Resource Sharing misconfigurations</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-warning">Medium</span></p>
                  <small className="text-muted">Tests for insecure CORS policies</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-primary mb-3">
                <div className="card-header bg-primary text-white">
                  <strong>Protocol Security Checks</strong> <span className="badge bg-warning ms-2">SU</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> SNMP, FTP, SMB, RDP protocol security</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-warning">Medium</span></p>
                  <small className="text-muted">Tests various network protocols for security issues</small>
                </div>
              </div>
            </div>
          </div>

          {/* Vulnerability Assessment Tools */}
          <h5><i className="fa fa-bug text-danger me-2"></i>Vulnerability Assessment Tools</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="card border-danger mb-3">
                <div className="card-header bg-danger text-white">
                  <strong>Web Vulnerability Scanner</strong> <span className="badge bg-warning ms-2">SU</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Common web vulnerabilities using Nikto scanner</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-warning">Medium</span></p>
                  <small className="text-muted">Comprehensive web application vulnerability scanning</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-danger mb-3">
                <div className="card-header bg-danger text-white">
                  <strong>SQL Injection Scanner</strong> <span className="badge bg-danger ms-2">AD</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> SQL injection vulnerabilities using SQLMap</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-danger">High</span></p>
                  <small className="text-muted">Automated SQL injection detection and exploitation</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-danger mb-3">
                <div className="card-header bg-danger text-white">
                  <strong>SSRF Check</strong> <span className="badge bg-danger ms-2">AD</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Server-Side Request Forgery vulnerabilities</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-danger">High</span></p>
                  <small className="text-muted">Tests for SSRF attack vectors</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-danger mb-3">
                <div className="card-header bg-danger text-white">
                  <strong>Web Vulnerability Fuzzer</strong> <span className="badge bg-warning ms-2">SU</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> XSS, LFI, and Open Redirect vulnerabilities</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-warning">Medium</span></p>
                  <small className="text-muted">Basic web vulnerability fuzzing with common payloads</small>
                </div>
              </div>
            </div>
          </div>

          {/* Cloud Security Tools */}
          <h5><i className="fa fa-cloud text-success me-2"></i>Cloud Security Tools</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="card border-success mb-3">
                <div className="card-header bg-success text-white">
                  <strong>Cloud Exposure Scanner</strong> <span className="badge bg-success ms-2">US</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Public S3 buckets and misconfigured cloud storage</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-success">Low</span></p>
                  <small className="text-muted">Scans for publicly accessible cloud storage</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-success mb-3">
                <div className="card-header bg-success text-white">
                  <strong>Cloud Security Scanner</strong> <span className="badge bg-danger ms-2">AD</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> AWS, Azure, GCP security configurations (requires credentials)</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-danger">High</span></p>
                  <small className="text-muted">Comprehensive cloud security assessment</small>
                </div>
              </div>
            </div>
          </div>

          {/* Exploitation Tools */}
          <h5><i className="fa fa-cogs text-warning me-2"></i>Exploitation Tools</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="card border-warning mb-3">
                <div className="card-header bg-warning text-dark">
                  <strong>CrackMapExec</strong> <span className="badge bg-danger ms-2">AD</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> SMB/WinRM/SSH service enumeration and credential testing</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-danger">High</span></p>
                  <small className="text-muted">Network service enumeration and credential validation</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-warning mb-3">
                <div className="card-header bg-warning text-dark">
                  <strong>Impacket Tools</strong> <span className="badge bg-danger ms-2">AD</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Windows protocol exploitation and credential extraction</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-danger">High</span></p>
                  <small className="text-muted">Advanced Windows protocol attacks</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-warning mb-3">
                <div className="card-header bg-warning text-dark">
                  <strong>Hydra / Medusa</strong> <span className="badge bg-danger ms-2">AD</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Credential brute-force and password spraying attacks</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-danger">High</span></p>
                  <small className="text-muted">Brute-force attacks against login services</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-warning mb-3">
                <div className="card-header bg-warning text-dark">
                  <strong>Responder / LLMNR</strong> <span className="badge bg-danger ms-2">AD</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> LLMNR/NBT-NS poisoning and credential capture</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-danger">High</span></p>
                  <small className="text-muted">Network protocol poisoning attacks</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-warning mb-3">
                <div className="card-header bg-warning text-dark">
                  <strong>Metasploit Framework</strong> <span className="badge bg-danger ms-2">AD</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Exploit framework for vulnerability validation</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-danger">High</span></p>
                  <small className="text-muted">Advanced exploitation and vulnerability validation</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-warning mb-3">
                <div className="card-header bg-warning text-dark">
                  <strong>Enum4linux-ng / LDAP Recon</strong> <span className="badge bg-warning ms-2">SU</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Linux/Windows enumeration via SMB and LDAP</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-warning">Medium</span></p>
                  <small className="text-muted">System enumeration and information gathering</small>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-warning mb-3">
                <div className="card-header bg-warning text-dark">
                  <strong>BloodHound / SharpHound</strong> <span className="badge bg-warning ms-2">SU</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Active Directory graph analysis and attack paths</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-warning">Medium</span></p>
                  <small className="text-muted">AD enumeration and privilege escalation path discovery</small>
                </div>
              </div>
            </div>
          </div>

          {/* Email Security Tools */}
          <h5><i className="fa fa-envelope text-info me-2"></i>Email Security Tools</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="card border-info mb-3">
                <div className="card-header bg-info text-white">
                  <strong>Email Security Scanner</strong> <span className="badge bg-warning ms-2">SU</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> SPF, DKIM, DMARC records and email security configuration</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-warning">Medium</span></p>
                  <small className="text-muted">Email security assessment and anti-spoofing checks</small>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Tools */}
          <h5><i className="fa fa-chart-line text-info me-2"></i>Performance Tools</h5>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="card border-info mb-3">
                <div className="card-header bg-info text-white">
                  <strong>Performance Analyzer</strong> <span className="badge bg-success ms-2">US</span>
                </div>
                <div className="card-body">
                  <p><strong>What it tests:</strong> Website performance and response times</p>
                  <p><strong>Risk Level:</strong> <span className="badge bg-success">Low</span></p>
                  <small className="text-muted">Analyzes website performance metrics</small>
                </div>
              </div>
            </div>
          </div>

          <div className="alert alert-warning mt-4">
            <i className="fa fa-exclamation-triangle me-2"></i>
            <strong>Important Security Notice:</strong>
            <ul className="mt-2 mb-0">
              <li><strong>High-risk tools</strong> may trigger security alerts and should only be used on authorized targets</li>
              <li><strong>Exploitation tools</strong> require Admin privileges and should be used with extreme caution</li>
              <li><strong>Always obtain proper authorization</strong> before running scans against any target</li>
              <li><strong>Some tools require credentials</strong> (like Cloud Security Scanner) to function properly</li>
            </ul>
          </div>
        </CardBody>
      </Card>

      {/* Step-by-Step Guide */}
      <Card className="mb-4">
        <CardHeader>
          <h4 className="mb-0">
            <i className="bi bi-list-ol me-2"></i>
            Step-by-Step Usage Guide
          </h4>
        </CardHeader>
        <CardBody>
          <h5>1. Accessing Small Pentest</h5>
          <ol>
            <li>Navigate to the Small Pentest section from the main menu</li>
            <li>Ensure you have the appropriate user role for the tools you want to use</li>
            <li>Verify your user information is displayed correctly</li>
          </ol>

          <h5>2. Selecting Security Tools</h5>
          <ol>
            <li><strong>Choose Tools:</strong> Expand tool categories to view available scanners</li>
            <li><strong>Check Permissions:</strong> Tools show role badges (US/SU/AD) indicating required permissions</li>
            <li><strong>Use Presets:</strong> Click quick preset buttons for common tool combinations</li>
            <li><strong>Bulk Selection:</strong> Use "Select All" or "Clear All" for quick tool management</li>
            <li><strong>Review Selection:</strong> Check the tool summary at the bottom of the selection panel</li>
          </ol>

          <h5>3. Configuring the Scan</h5>
          <ol>
            <li><strong>Enter Target URL:</strong> Input a valid URL starting with http:// or https://</li>
            <li><strong>Validate Input:</strong> Ensure the URL format is correct</li>
            <li><strong>Review Tools:</strong> Confirm your tool selection (minimum 1 tool required)</li>
            <li><strong>Check Status:</strong> Verify no other scans are currently running</li>
          </ol>

          <h5>4. Running the Scan</h5>
          <ol>
            <li><strong>Start Scan:</strong> Click the "Start Scan" button</li>
            <li><strong>Monitor Progress:</strong> Watch the progress bar and real-time logs</li>
            <li><strong>View Logs:</strong> Monitor the terminal-style log output for detailed information</li>
            <li><strong>Wait for Completion:</strong> Allow the scan to complete (duration varies by tools selected)</li>
          </ol>

          <h5>5. Analyzing Results</h5>
          <ol>
            <li><strong>View Results Table:</strong> Completed scans appear in the results table</li>
            <li><strong>Expand Scan Details:</strong> Click the arrow to view scan files</li>
            <li><strong>Access Files:</strong> Click on individual files to view detailed results</li>
            <li><strong>Filter Results:</strong> Use the search filter to find specific scans</li>
            <li><strong>Sort Data:</strong> Click column headers to sort results</li>
          </ol>

          <h5>6. Managing Scan Data (Admin/Superuser)</h5>
          <ol>
            <li><strong>Generate Reports:</strong> Click the PDF button for AI-generated reports</li>
            <li><strong>Delete Scans:</strong> Remove individual or multiple scans</li>
            <li><strong>Bulk Operations:</strong> Select multiple scans for batch deletion</li>
            <li><strong>Clear Display:</strong> Use "Clear Table" to reset the display</li>
          </ol>
        </CardBody>
      </Card>

      {/* Features and Functions */}
      <Card className="mb-4">
        <CardHeader>
          <h4 className="mb-0">
            <i className="bi bi-gear me-2"></i>
            Features and Functions
          </h4>
        </CardHeader>
        <CardBody>
          <h5>Scan Form Features:</h5>
          <ul>
            <li><strong>URL Validation:</strong> Automatic validation of target URLs</li>
            <li><strong>Tool Selection:</strong> Interactive tool picker with permission checking</li>
            <li><strong>User Context:</strong> Display of current user and role information</li>
            <li><strong>Real-time Feedback:</strong> Button states reflect scan status</li>
          </ul>

          <h5>Progress Monitoring:</h5>
          <ul>
            <li><strong>Progress Bar:</strong> Visual indication of scan completion percentage</li>
            <li><strong>Live Logs:</strong> Real-time log output with color-coded message types</li>
            <li><strong>Status Updates:</strong> Automatic updates during scan execution</li>
            <li><strong>Error Handling:</strong> Clear error messages and warnings</li>
          </ul>

          <h5>Results Management:</h5>
          <ul>
            <li><strong>Sortable Columns:</strong> Click headers to sort by any field</li>
            <li><strong>Search Filter:</strong> Text-based filtering across all columns</li>
            <li><strong>Pagination:</strong> Configurable page sizes and navigation</li>
            <li><strong>File Access:</strong> Direct access to scan result files</li>
            <li><strong>Bulk Selection:</strong> Multi-select for batch operations</li>
          </ul>

          <h5>Advanced Features (Admin/Superuser):</h5>
          <ul>
            <li><strong>PDF Generation:</strong> AI-powered report creation</li>
            <li><strong>Data Management:</strong> Scan deletion and cleanup</li>
            <li><strong>System Control:</strong> Table clearing and refresh operations</li>
            <li><strong>User Monitoring:</strong> View scans from all users</li>
          </ul>
        </CardBody>
      </Card>

      {/* Troubleshooting */}
      <Card className="mb-4">
        <CardHeader>
          <h4 className="mb-0">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Troubleshooting
          </h4>
        </CardHeader>
        <CardBody>
          <h5>Common Issues and Solutions:</h5>
          
          <h6>"No tools selected" Error:</h6>
          <ul>
            <li>Ensure at least one security tool is selected before starting a scan</li>
            <li>Check that you have permission to use the selected tools</li>
            <li>Verify tools are not disabled due to role restrictions</li>
          </ul>

          <h6>"Invalid URL" Error:</h6>
          <ul>
            <li>Ensure URL starts with http:// or https://</li>
            <li>Verify the target is accessible from your network</li>
            <li>Check for typos in the URL format</li>
          </ul>

          <h6>Scan Stuck or Not Progressing:</h6>
          <ul>
            <li>Check the progress logs for error messages</li>
            <li>Verify network connectivity to the target</li>
            <li>Consider reducing the number of selected tools</li>
            <li>Contact an administrator if the issue persists</li>
          </ul>

          <h6>Permission Denied Errors:</h6>
          <ul>
            <li>Verify your user role has access to the selected tools</li>
            <li>Contact an administrator to upgrade your permissions</li>
            <li>Use tools appropriate for your role level</li>
          </ul>

          <h6>Results Not Loading:</h6>
          <ul>
            <li>Click the refresh button to reload scan data</li>
            <li>Check your network connection</li>
            <li>Verify the scan completed successfully</li>
            <li>Clear browser cache if issues persist</li>
          </ul>
        </CardBody>
      </Card>

      {/* Best Practices */}
      <Card className="mb-4">
        <CardHeader>
          <h4 className="mb-0">
            <i className="bi bi-star me-2"></i>
            Best Practices
          </h4>
        </CardHeader>
        <CardBody>
          <h5>Security Considerations:</h5>
          <ul>
            <li><strong>Authorization:</strong> Only scan targets you own or have explicit permission to test</li>
            <li><strong>Scope:</strong> Clearly define the scope of your penetration testing</li>
            <li><strong>Documentation:</strong> Keep records of all scanning activities</li>
            <li><strong>Responsible Disclosure:</strong> Report vulnerabilities through proper channels</li>
          </ul>

          <h5>Performance Optimization:</h5>
          <ul>
            <li><strong>Tool Selection:</strong> Choose tools appropriate for your target and objectives</li>
            <li><strong>Resource Management:</strong> Avoid running too many intensive tools simultaneously</li>
            <li><strong>Timing:</strong> Schedule scans during appropriate time windows</li>
            <li><strong>Monitoring:</strong> Watch progress logs for early detection of issues</li>
          </ul>

          <h5>Data Management:</h5>
          <ul>
            <li><strong>Regular Cleanup:</strong> Remove old scan data to maintain system performance</li>
            <li><strong>Result Analysis:</strong> Review and analyze results promptly after completion</li>
            <li><strong>Report Generation:</strong> Use PDF reports for formal documentation</li>
            <li><strong>Backup:</strong> Save important results before deletion</li>
          </ul>
        </CardBody>
      </Card>

      {/* API Integration */}
      <Card className="mb-4">
        <CardHeader>
          <h4 className="mb-0">
            <i className="bi bi-code me-2"></i>
            API Integration
          </h4>
        </CardHeader>
        <CardBody>
          <h5>Available Endpoints:</h5>
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Method</th>
                  <th>Endpoint</th>
                  <th>Description</th>
                  <th>Required Role</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>GET</code></td>
                  <td><code>/api/scans</code></td>
                  <td>Retrieve all scan results</td>
                  <td>User</td>
                </tr>
                <tr>
                  <td><code>POST</code></td>
                  <td><code>/api/scan</code></td>
                  <td>Start a new security scan</td>
                  <td>User</td>
                </tr>
                <tr>
                  <td><code>GET</code></td>
                  <td><code>/api/results/files/scanId</code></td>
                  <td>Get scan result files</td>
                  <td>User</td>
                </tr>
                <tr>
                  <td><code>DELETE</code></td>
                  <td><code>/api/scan/scanId</code></td>
                  <td>Delete individual scan</td>
                  <td>Superuser</td>
                </tr>
                <tr>
                  <td><code>DELETE</code></td>
                  <td><code>/api/scans</code></td>
                  <td>Bulk delete scans</td>
                  <td>Admin</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {/* Support */}
      <Card className="mb-4">
        <CardHeader>
          <h4 className="mb-0">
            <i className="bi bi-question-circle me-2"></i>
            Support and Resources
          </h4>
        </CardHeader>
        <CardBody>
          <h5>Getting Help:</h5>
          <ul>
            <li><strong>Documentation:</strong> Refer to this comprehensive guide for detailed information</li>
            <li><strong>Progress Logs:</strong> Check real-time logs for troubleshooting information</li>
            <li><strong>Error Messages:</strong> Pay attention to system error messages and warnings</li>
            <li><strong>Administrator:</strong> Contact your system administrator for permission issues</li>
          </ul>

          <h5>Additional Resources:</h5>
          <ul>
            <li><strong>Security Best Practices:</strong> Follow industry-standard penetration testing guidelines</li>
            <li><strong>Tool Documentation:</strong> Refer to individual tool documentation for specific features</li>
            <li><strong>Training:</strong> Consider security training courses for advanced usage</li>
            <li><strong>Community:</strong> Engage with cybersecurity communities for knowledge sharing</li>
          </ul>
        </CardBody>
      </Card>
    </div>
  );
};

export default SmallPentestDocumentation;